import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Rendimiento»: one time window and the six readings the page paints. Each schema mirrors a view of
 * `performance-views.ts` (backend), and `shared-performance-screen.type-assertions.ts` ties them in both directions, so
 * the two cannot drift. Every object is strict: a field that is not named here does not travel.
 */

export const PERFORMANCE_TIME_RANGES = ['1h', '6h', '24h', '7d', '30d'] as const

export const performanceQuerySchema = z.strictObject({
  timeRange: z.enum(PERFORMANCE_TIME_RANGES).default('24h'),
})

/** The window every section reads: ONE instant per request, resolved before any section. */
export const performanceRangeSchema = z.strictObject({
  timeRange: z.enum(PERFORMANCE_TIME_RANGES),
  start: z.string(),
  end: z.string(),
  groupBy: z.enum(['minute', 'hour', 'day']),
})

export const performanceHttpSummarySchema = z.strictObject({
  totalRequests: z.number(),
  avgLatency: z.number(),
  p95Latency: z.number(),
  errorRate: z.number(),
  requestsPerSecond: z.number(),
})

export const performanceDatabaseSchema = z.strictObject({
  totalQueries: z.number(),
  avgQueryTime: z.number(),
  slowQueries: z.number(),
  cacheHitRate: z.number(),
  queriesByTable: z.array(
    z.strictObject({
      tableName: z.string(),
      count: z.number(),
      avgDuration: z.number(),
      maxDuration: z.number(),
    }),
  ),
})

export const performanceExternalSchema = z.strictObject({
  totalCalls: z.number(),
  successRate: z.number(),
  avgLatency: z.number(),
  services: z.array(
    z.strictObject({
      service: z.string(),
      calls: z.number(),
      successRate: z.number(),
      avgLatency: z.number(),
    }),
  ),
})

export const performanceSystemSchema = z.strictObject({
  memory: z.strictObject({ avg: z.number(), max: z.number(), current: z.number() }),
  cpu: z.strictObject({ avgLoadAvg: z.array(z.number()) }),
})

export const performanceLatencyPointSchema = z.strictObject({
  timestamp: z.string(),
  value: z.number(),
})

export const performanceAlertRowSchema = z.strictObject({
  id: z.string(),
  /** The table has a `CHECK` with these eight values (migration 0124). */
  type: z.enum([
    'high_latency',
    'high_error_rate',
    'low_cache_hit',
    'high_memory',
    'high_cpu',
    'queue_backlog',
    'db_slow_query',
    'external_api_failure',
  ]),
  severity: z.enum(['info', 'warning', 'critical']),
  metricName: z.string(),
  threshold: z.number(),
  comparison: z.enum(['greater_than', 'less_than', 'equals']),
  enabled: z.boolean(),
  notificationChannels: z.array(z.string()),
  cooldownMinutes: z.number(),
  /** `null`: it never fired, or the stored date cannot be read. */
  lastTriggered: z.string().nullable(),
})

export const adminPerformanceScreen = defineScreen({
  id: 'admin.performance',
  path: '/api/screens/admin/performance',
  access: 'super_admin',
  query: performanceQuerySchema,
  response: z.strictObject({
    // First key: the screen composes in series and in this order, and `range` resolves the window of the others
    range: performanceRangeSchema,
    http: screenSection(performanceHttpSummarySchema),
    database: screenSection(performanceDatabaseSchema),
    external: screenSection(performanceExternalSchema),
    system: screenSection(performanceSystemSchema),
    latency: screenSection(z.array(performanceLatencyPointSchema)),
    alerts: screenSection(z.array(performanceAlertRowSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.performance'],
  budget: { requests: 1, statements: 32, bytes: 40_000, p95Ms: 800 },
  summary:
    'System performance for one time range: HTTP summary, database, external APIs, system resources, API latency series and alert configurations',
})

export type PerformanceTimeRange = (typeof PERFORMANCE_TIME_RANGES)[number]
export type PerformanceScreenRange = z.infer<typeof performanceRangeSchema>
export type PerformanceHttpSummary = z.infer<typeof performanceHttpSummarySchema>
export type PerformanceDatabaseMetrics = z.infer<typeof performanceDatabaseSchema>
export type PerformanceExternalMetrics = z.infer<typeof performanceExternalSchema>
export type PerformanceSystemMetrics = z.infer<typeof performanceSystemSchema>
export type PerformanceLatencyPoint = z.infer<typeof performanceLatencyPointSchema>
export type PerformanceAlertRow = z.infer<typeof performanceAlertRowSchema>
export type AdminPerformanceQuery = ScreenQuery<typeof adminPerformanceScreen>
export type AdminPerformanceData = ScreenData<typeof adminPerformanceScreen>
